import { useQuery } from '@tanstack/react-query'
import './WhatsNew.css'
import { observer } from 'mobx-react-lite'
import { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useState } from 'react'
import semver from 'semver'
import { StaticAlert } from '~/Components/Alert.js'
import { Modal } from '~/Components/Modal.js'
import { TabArea } from '~/Components/TabArea.js'
import { useLocalStorage } from '~/Hooks/useLocalStorage.js'
import { makeAbsolutePath } from '~/Resources/util.js'
import { RootAppStoreContext } from '~/Stores/RootAppStore.js'
import { shouldAutoOpenWizard } from '~/Wizard/Constants.js'
import { MyErrorBoundary } from '../Resources/Error.js'
import { DocsContent } from './DocsContent.js'

interface WhatsNewPage {
	version: string
	label: string
	file: string
}

export interface WhatsNewModalRef {
	show(): void
}

export const WhatsNewModal = observer(
	forwardRef<WhatsNewModalRef>(function HelpModal(_props, ref) {
		const { userConfig, wizardOpen } = useContext(RootAppStoreContext)

		const [show, setShow] = useState(false)
		const [selectedVersion, setSelectedVersion] = useState<string | undefined>(undefined)
		const [storedLatest, setStoredLatest] = useLocalStorage<string | undefined>('whatsnew', undefined, { sync: true })

		// The setup wizard takes priority on launch: don't auto-pop What's New while it is pending or open.
		// Once the wizard finishes (it bumps setup_wizard and clears wizardOpen), this re-evaluates and shows.
		// Wait for the config to load first, so we know whether the wizard is pending before deciding.
		const setupWizardVersion = userConfig.properties?.setup_wizard
		const configLoaded = setupWizardVersion !== undefined
		const isWizardPending = configLoaded && shouldAutoOpenWizard(setupWizardVersion)
		const isWizardActive = wizardOpen.get()

		// Load pages manifest using proper React Query
		const {
			data: pages,
			error,
			isPending,
		} = useQuery<WhatsNewPage[]>({
			queryKey: ['whatsnew_manifest'],
			queryFn: async () => {
				const response = await fetch(makeAbsolutePath('/whatsnew/manifest.json'))
				if (!response.ok) {
					throw new Error(`Failed to load manifest: ${response.statusText}`)
				}
				return response.json()
			},
			retry: 2,
			staleTime: Infinity, // Manifest doesn't change during session
		})

		const latestPage = pages?.[0]

		// Check if we should auto-show the modal for new versions
		useEffect(() => {
			if (!configLoaded || isWizardPending || isWizardActive) return
			if (latestPage && (!storedLatest || semver.lt(storedLatest, latestPage.version))) {
				setShow(true)
				console.log('New version detected, showing WhatsNewModal')
			}
		}, [latestPage, storedLatest, configLoaded, isWizardPending, isWizardActive])

		// Set initial selected version when pages load
		useEffect(() => {
			if (pages && pages.length > 0 && !selectedVersion) {
				setSelectedVersion(pages[0].file)
			}
		}, [pages, selectedVersion])

		const selectedPage = selectedVersion && pages?.find((page) => page.file === selectedVersion)

		const onClosed = useCallback(() => {
			if (latestPage) {
				setStoredLatest(latestPage.version)
			}
		}, [setStoredLatest, latestPage])

		useImperativeHandle(
			ref,
			() => ({
				show() {
					setShow(true)
				},
			}),
			[]
		)

		return (
			<Modal.Root
				open={show}
				onOpenChange={setShow}
				onOpenChangeComplete={(open) => {
					if (!open) onClosed()
				}}
			>
				<Modal.Portal>
					<Modal.Backdrop />
					<Modal.Viewport>
						<Modal.Popup size="lg" scrollable className="modal-whatsnew">
							<Modal.Header closeButton>
								<Modal.Title>What's New in Companion</Modal.Title>
							</Modal.Header>
							<Modal.Body>
								{isPending && <div className="p-4">Loading...</div>}
								{error && (
									<StaticAlert color="danger">
										Failed to load What's New content: {error instanceof Error ? error.message : 'Unknown error'}
									</StaticAlert>
								)}
								{pages && pages.length > 0 && (
									<TabArea.Root value={selectedVersion} onValueChange={setSelectedVersion}>
										<TabArea.List>
											{pages.map((page) => (
												<TabArea.Tab key={page.version} value={page.file}>
													{page.label}
												</TabArea.Tab>
											))}
										</TabArea.List>
										{selectedPage && (
											<TabArea.Panel value={selectedPage.file}>
												<MyErrorBoundary>
													<DocsContent file={selectedPage.file} />
												</MyErrorBoundary>
											</TabArea.Panel>
										)}
									</TabArea.Root>
								)}
							</Modal.Body>
						</Modal.Popup>
					</Modal.Viewport>
				</Modal.Portal>
			</Modal.Root>
		)
	})
)
